import { colors } from "@mui/material";
import { TObject } from "corexxx";

export function generateTradingTimes(): { time: string; price: number | null }[] {
    const times: { time: string; price: number | null }[] = [];

    let startHour = 14, startMinute = 30;
    let endHour = 21, endMinute = 0;
    let interval = 1;

    let currentDate = new Date();
    currentDate.setHours(startHour, startMinute, 0, 0);

    let endDate = new Date();
    endDate.setHours(endHour, endMinute, 0, 0);

    while (currentDate <= endDate) {
        const hh = String(currentDate.getHours()).padStart(2, '0');
        const mm = String(currentDate.getMinutes()).padStart(2, '0');
        times.push({ time: `${hh}:${mm}`, price: null });
        currentDate.setMinutes(currentDate.getMinutes() + interval);
    }
    return times;
}


export function mergeTradingData(baseData: { time: string; price: number | null }[], overrideData: { time: string; price: number | null }[]): { time: string; price: number | null }[] {
    const overrideMap = new Map(overrideData.map(entry => [entry.time, entry.price]));

    return baseData.map(entry => ({
        time: entry.time,
        price: overrideMap.has(entry.time) ? overrideMap.get(entry.time)! : entry.price
    }));
}




export function convertToQuote(resp: any) {
    let res: TObject[] = []
    try {
        let res = Object.values(resp.state.data?.out || {}).map((x: any) => ({
            symbol: x.symbol as string,
            ltp: parseFloat(x.quote?.lastPrice.toFixed(2)),
            pchange: x.quote?.markPercentChange,
            high: x.quote?.highPrice,
            low: x.quote?.lowPrice
        }));
        res.sort((a: any, b: any) => a.pchange > b.pchange ? -1 : 1)
        return res;
    } catch (e) {

    }
    return res;
}

export const calculateNetDelta = (data: any) => {
    if (!data) return null;

    const calls = data.callExpDateMap
        ? Object.values(data.callExpDateMap).flatMap((date: any) => Object.values(date).flat())
        : [];
    const puts = data.putExpDateMap
        ? Object.values(data.putExpDateMap).flatMap((date: any) => Object.values(date).flat())
        : [];

    // Adjust net delta calculation to avoid extreme values
    const totalCallDelta = calls.reduce((acc: number, opt: any) => {
        const volume = opt.totalVolume || 1;  // Prevent division by zero
        const openInterest = opt.openInterest || 1; // Prevent over-weighting if OI is missing
        return acc + ((opt.delta || 0) * volume) / openInterest; // Normalize by open interest
    }, 0);

    const totalPutDelta = puts.reduce((acc: number, opt: any) => {
        const volume = opt.totalVolume || 1;
        const openInterest = opt.openInterest || 1;
        return acc + ((opt.delta || 0) * volume) / openInterest;
    }, 0);

    const netDelta = (totalCallDelta + totalPutDelta).toFixed(2);  // Net delta remains balanced

    // ✅ Calculate total volume for Put & Call
    const totalCallVolume = calls.reduce((acc: number, opt: any) => acc + (opt.totalVolume || 0), 0);
    const totalPutVolume = puts.reduce((acc: number, opt: any) => acc + (opt.totalVolume || 0), 0);

    // ✅ Calculate Put/Call Ratio
    const ratio = totalCallVolume > 0 ? (totalPutVolume / totalCallVolume).toFixed(2) : "N/A";

    // ✅ Sort and get top 5 options
    const topCalls = calls.sort((a: any, b: any) => b.totalVolume - a.totalVolume).slice(0, 5);
    const topPuts = puts.sort((a: any, b: any) => b.totalVolume - a.totalVolume).slice(0, 5);

    // ✅ High Gamma Threshold & Filtering
    const highGammaThreshold = 0.10;
    const highGammaCalls = calls.filter((opt: any) => opt.gamma > highGammaThreshold);
    const highGammaPuts = puts.filter((opt: any) => opt.gamma > highGammaThreshold);


    return {
        highGammaCalls: highGammaCalls,
        highGammaPuts: highGammaPuts,
        netDelta: parseFloat(netDelta), totalCallVolume: totalCallVolume, totalPutVolume: totalPutVolume, ratio: ratio, topCalls: topCalls, topPuts: topPuts
    };;
};

export function getFormattedDate(num: number): string {
    const date = new Date(num);

    // Get day with ordinal suffix
    const day = date.getDate();
    const ordinalSuffix = (n: number) => {
        if (n > 3 && n < 21) return "th";
        switch (n % 10) {
            case 1: return "st";
            case 2: return "nd";
            case 3: return "rd";
            default: return "th";
        }
    };

    const formattedDay = `${day}${ordinalSuffix(day)}`;

    // Get month abbreviation
    const month = date.toLocaleString("en-GB", { month: "short" });

    // Get weekday
    const weekday = date.toLocaleString("en-GB", { weekday: "long" });

    // Get time in HH:MM format
    const time = date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    return `${formattedDay} ${month}, ${weekday} ${time}`;
}

// Return last Trdaying day in dd/mm/yyyy
export function getLastTradingDay() {
    // Convert London time to New York time (Eastern Time)
    const newYorkTime = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));

    // Get the day of the week in New York
    let dayOfWeek = newYorkTime.getDay();

    // If today is Sunday in New York, move back to Friday
    if (dayOfWeek === 0) newYorkTime.setDate(newYorkTime.getDate() - 2);
    // If today is Saturday in New York, move back to Friday
    else if (dayOfWeek === 6) newYorkTime.setDate(newYorkTime.getDate() - 1);

    // Format as dd/mm/yyyy
    const day = String(newYorkTime.getDate()).padStart(2, '0');
    const month = String(newYorkTime.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = newYorkTime.getFullYear();

    return `${day}/${month}/${year}`;
}


export function convertEpochTo2HrFormat(epochMs: number) {
    return (new Date(epochMs)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}


export function processtickerHistoryResp(resp: TObject): TObject[] {
    if (resp.candles.length < 1) return [];
    let pclose = resp.candles[0].close;
    let res = resp.candles.map((x: any) => {
        let pchange = ((x.close - pclose) / pclose) * 100;
        pclose = x.close;
        return {
            ...x,
            time: new Date(x.datetime),
            price: x.close,
            pchange: pchange,
            color: getColor(pchange)
        }
    });
    return res;
}

function getColor(percentageChange: number) {
    if (percentageChange > 5) return "#004d00"; // Dark Green
    if (percentageChange > 2) return "#008000"; // Deep Green
    if (percentageChange > 0.5) return "#66c266"; // Mid Green
    if (percentageChange > 0) return "#b3e6b3"; // Light Green
    if (percentageChange < -5) return "#660000"; // Dark Red
    if (percentageChange < -2) return "#cc0000"; // Deep Red
    if (percentageChange < -0.5) return "#ff6666"; // Mid Red
    if (percentageChange < 0) return "#ffcccc"; // Light Red
    return "#cccccc"; // Neutral Gray
}